import "../styles/default.scss";

import React, { useRef, useState, useEffect } from "react";
import { withRouter } from "react-router-dom";
import { compose } from "redux";

import withTranslations from "../../../../../../hocs/withTranslations";
import withUserData from "../../../../../../hocs/withUserData";
import config from "../../../../../../config";
import LandingTypography from "../../../components/LandingTypography";
import FormatCurrency from "../../Homepage/components/FormatCurrency";
import useOnPaymentButtonClick, {
  getPaymentMethodPricing,
  useIsProceedToPayment
} from "../utils/useOnPaymentButtonClick";
import { createPortal } from "react-dom";
import PaymentModal from "./PaymentModal";
import SelectedPlanName from "./SelectedPlanName";
import SelectedPlanPrice from "./SelectedPlanPrice";
import hasSubtractedAmount from "../utils/hasSubtractedAmount";
import PaymentMethods from "./PaymentMethods";
import hasSupportedPaymentMethodFunction from "../utils/hasSupportedPaymentMethod";
import isSelectedPackageSubscriptionFunction from "../utils/isSelectedPackageSubscription";
import Checkbox from "../../../../../../lib/Checkbox/Checkbox";
import InvoiceFrom from "./InvoiceForms/components/InvoiceForm";
import withInvoices from "../../../../../../hocs/withInvoices";
import SelectedPlanDescription from "./SelectedPlanDescription";
import PaymentButton from "./PaymentButton";
import { resolveInvoiceFormDataForPayment } from "../../../../../../lib/payment/resolveInvoiceFormDataForPayment";

const PaymentBar = ({
  selectedPlan,
  currentCurrencyOfferByPeriod,
  availablePaymentMethods,
  locale,
  translate,
  history,
  setSelectedPaymentMethod,
  selectedPaymentMethod,
  finalizePayment,
  paymentFormLoading,
  paymentDetailsLoading,
  paymentDetails,
  userData,
  fetchSavedInvoiceData,
  invoicesLoading,
  savedInvoicesEnabled
}) => {
  const invoiceFromRef = useRef(null);
  const { onPaymentButtonClick } = useOnPaymentButtonClick({
    finalizePayment,
    selectedPaymentMethod,
    currentCurrencyOfferByPeriod,
    selectedPlan,
    locale,
    userData,
    history
  });
  const [showModal, setShowModal] = useState(false);
  const [showInvoiceForm, setShowInvoiceForm] = useState(false);
  const invoicesEnabled = config.invoicesEnabled;

  const isProceedToPayment = useIsProceedToPayment();

  useEffect(() => {
    if (invoicesEnabled) {
      fetchSavedInvoiceData();
    }
  }, []);

  useEffect(() => {
    if (!invoicesEnabled) {
      return;
    }

    setShowInvoiceForm(savedInvoicesEnabled);
  }, [savedInvoicesEnabled, invoicesEnabled]);

  if (
    isProceedToPayment ||
    paymentDetailsLoading ||
    (invoicesEnabled && invoicesLoading)
  ) {
    return null;
  }

  const hasSupportedPaymentMethod = hasSupportedPaymentMethodFunction({
    availablePaymentMethods
  });
  const paymentMethodPricing = getPaymentMethodPricing({
    selectedPaymentMethod,
    currentCurrencyOfferByPeriod
  });
  const isSelectedPackageSubscription = isSelectedPackageSubscriptionFunction({
    selectedPaymentMethod
  });

  const selectedPlanName = <SelectedPlanName name={selectedPlan.name} />;
  const price = (
    <SelectedPlanPrice
      paymentMethodPricing={paymentMethodPricing}
      hasSubtractedAmount={hasSubtractedAmount(paymentMethodPricing)}
    />
  );
  const description = (
    <SelectedPlanDescription
      currentCurrencyOfferByPeriod={currentCurrencyOfferByPeriod}
      isSelectedPackageSubscription={isSelectedPackageSubscription}
      paymentDetails={paymentDetails}
      selectedPaymentMethod={selectedPaymentMethod}
      selectedPlan={selectedPlan}
    />
  );

  return (
    <LandingTypography className={"payment-bar landing-typography_dark"}>
      <form>
        <div className={"py-4 bg-gray-1"}>
          <div className={"container"}>
            <div className={"flex gap-18 align-center"}>
              {selectedPlanName}
              {price}
              {invoicesEnabled ? (
                <div className={"ml-auto"}>
                  <Checkbox
                    label={translate("plans_payment-bar_invoice")}
                    className={"text-black-8"}
                    checked={showInvoiceForm}
                    onClick={() => {
                      setShowInvoiceForm(prevState => !prevState);
                    }}
                  />
                </div>
              ) : null}
            </div>
            {invoicesEnabled && showInvoiceForm ? (
              <div className={"mt-6 mb-2"}>
                <InvoiceFrom
                  locale={locale}
                  translate={translate}
                  ref={invoiceFromRef}
                />
              </div>
            ) : null}
          </div>
        </div>
        <div className={"bg-white py-6"}>
          {paymentMethodPricing?.overpayment_amount &&
          Number(paymentMethodPricing.order_amount) === 0 ? (
            <div className={"container"}>
              <strong className={"block text-sm text-bold text-black mb-2"}>
                {translate("plans_payment-bar_overpayment-title")}
              </strong>
              <p
                className={"text-xs text-black-8 mb-4 max-w-2xl.1"}
                dangerouslySetInnerHTML={{
                  __html: translate("plans_payment-bar_overpayment-paragraph", {
                    overpaymentAmount: FormatCurrency({
                      locale,
                      value: paymentMethodPricing.overpayment_amount
                    })
                  })
                }}
              ></p>
            </div>
          ) : null}
          <div className={"container flex gap-4 align-center justify-between"}>
            <PaymentMethods
              selectedPaymentMethod={selectedPaymentMethod}
              setSelectedPaymentMethod={setSelectedPaymentMethod}
              currentCurrencyOfferByPeriod={currentCurrencyOfferByPeriod}
              hasSupportedPaymentMethod={hasSupportedPaymentMethod}
              availablePaymentMethods={availablePaymentMethods}
            />
            <div
              className={
                "flex flex-wrap-reverse align-center gap-4 w-full justify-end ml-3"
              }
            >
              {description}
              <PaymentButton
                selectedPlan={selectedPlan}
                onPaymentButtonClick={onPaymentButtonClick}
                userData={userData}
                setShowModal={setShowModal}
                selectedPaymentMethod={selectedPaymentMethod}
                paymentFormLoading={paymentFormLoading}
                paymentDetails={paymentDetails}
                currentCurrencyOfferByPeriod={currentCurrencyOfferByPeriod}
                invoiceFromRef={invoiceFromRef}
                showInvoiceForm={showInvoiceForm}
              />
            </div>
          </div>
        </div>
      </form>
      {showModal
        ? createPortal(
            <PaymentModal
              selectedPlanName={selectedPlanName}
              price={price}
              description={description}
              selectedPlan={selectedPlan}
              onPaymentButtonClick={async () => {
                const invoiceFormData = await resolveInvoiceFormDataForPayment({
                  showInvoiceForm,
                  invoiceFormRef: invoiceFromRef
                });

                if (invoiceFormData === false) {
                  return;
                }

                onPaymentButtonClick({ invoiceFormData });
              }}
              hideModal={() => setShowModal(false)}
            />,
            document.body
          )
        : null}
    </LandingTypography>
  );
};

export default compose(
  withTranslations,
  withRouter,
  withUserData,
  withInvoices
)(PaymentBar);
